import React from "react";
import { FieldNode } from "./fieldNodes";
import { Grid, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import { renderFieldNodes } from "./renderFieldNodes";

interface ExportToCSVAsyncProps {
    fields: FieldNode[];
    onExport: (columns: string[]) => void | Promise<void>;
    buttonText?: string;
    title?: string;
    filename?: string;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    initialSelectedKeys?: Iterable<string>;
}

/**
 * When you have an endpoint or function that returns a blob, you can use this component to export it as a CSV.
 */
export function ExportToCSVAsync(props: ExportToCSVAsyncProps) {
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const [selectedKeys, setSelectedKeys] = React.useState(new Set<string>());
    const [isLoading, setIsLoading] = React.useState(false);

    const isOpen = props.open !== undefined ? props.open : isDialogOpen;
    const initialSelectedKeysRef = React.useRef(props.initialSelectedKeys);
    initialSelectedKeysRef.current = props.initialSelectedKeys;

    React.useEffect(() => {
        if (isOpen) {
            setSelectedKeys(new Set(initialSelectedKeysRef.current ?? []));
        }
    }, [isOpen]);

    return (
        <React.Fragment>
            {props.open === undefined && (
                <AcxButton color="white" onClick={() => setIsDialogOpen(true)}>
                    {props.buttonText || "Export CSV"}
                </AcxButton>
            )}
            <AcxDialog
                isOpen={isOpen}
                onClose={() => {
                    setIsDialogOpen(false);
                    props.onOpenChange?.(false);
                }}
                title={props.title || "Export CSV"}
                dialogContentChildren={
                    <Grid container direction="column">
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>
                            Select columns to include in the export:
                        </Typography>
                        {renderFieldNodes(
                            props.fields,
                            selectedKeys,
                            setSelectedKeys,
                            0,
                        )}
                    </Grid>
                }
                contentWidth="600px"
                maxWidth="lg"
            >
                <AcxButton
                    color="primary"
                    loading={isLoading}
                    disabled={selectedKeys.size === 0}
                    onClick={async () => {
                        setIsLoading(true);

                        try {
                            await props.onExport?.(Array.from(selectedKeys));
                            setIsDialogOpen(false);
                            props.onOpenChange?.(false);
                        } catch (error) {
                            console.error("Error exporting CSV:", error);
                        } finally {
                            setIsLoading(false);
                        }
                    }}
                >
                    Download CSV
                </AcxButton>
            </AcxDialog>
        </React.Fragment>
    );
}
